/**
 * The consent page for "Sign in with RareMargin": an AI app (Claude, ChatGPT, Claude Code, MCP Inspector) asked to
 * connect through /oauth/authorize, which saved its request and sent the person here. They sign in if needed, choose
 * the company and the role the app gets, and are sent back to the app with a one-time code (or a refusal). The server
 * decided which app it is and where the answer goes; this page only shows it (apps/server/src/routes/oauth.ts).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, useApi } from '../lib/api';
import { titleCase } from '../lib/format';
import type { ConnectRequest } from '../lib/types';
import { sessionHint } from '../site/hooks';
import { Icons } from '../components/icons';
import { Button, cx, ErrorNote, Field, Loading, Select } from '../components/ui';
import { AuthFrame } from './Login';

type AppRole = 'VIEWER' | 'ACCOUNTANT';

const ROLES: { role: AppRole; label: string; summary: string; detail: string }[] = [
  { role: 'VIEWER', label: 'Viewer', summary: 'Read only', detail: 'Statements, ledgers, invoices, bills, bank matching, controls and close status.' },
  { role: 'ACCOUNTANT', label: 'Accountant', summary: 'Read and draft', detail: 'Also drafts journal entries and accruals. Each one waits in Approvals until a person approves it.' },
];

export function ConnectPage() {
  const [params] = useSearchParams();
  const id = params.get('request');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useApi<ConnectRequest>(id ? `/api/oauth/requests/${encodeURIComponent(id)}` : null, { retry: false, refetchOnWindowFocus: false });
  const [orgId, setOrgId] = useState<string | null>(null);
  const [role, setRole] = useState<AppRole>('VIEWER');
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [leaving, setLeaving] = useState<'allowed' | 'denied' | null>(null);

  // Not signed in: sign in (or up), then come back here.
  const signedOut = q.error instanceof ApiError && q.error.status === 401;
  const here = `/connect?request=${encodeURIComponent(id ?? '')}`;
  useEffect(() => {
    if (signedOut) navigate('/login', { replace: true, state: { next: here, notice: 'Sign in to RareMargin to connect the app.' } });
  }, [signedOut, navigate, here]);

  if (!id) {
    return (
      <AuthFrame title="Nothing to connect" subtitle="This page opens when an AI app asks to connect to RareMargin.">
        <p className="text-[13px] text-muted">Start from the app: add RareMargin as a connector, and it brings you here to sign in.</p>
      </AuthFrame>
    );
  }
  if (q.isLoading || signedOut) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loading label="Loading the request…" />
      </div>
    );
  }
  if (q.error || !q.data) {
    const gone = q.error instanceof ApiError && q.error.status === 410;
    return (
      <AuthFrame title={gone ? 'This request has expired' : 'Something went wrong'} subtitle={gone ? 'Sign-in requests last 15 minutes and work once.' : 'The request could not be loaded.'}>
        {gone ? <p className="text-[13px] text-muted">Go back to the app and connect RareMargin again.</p> : <ErrorNote error={q.error} />}
      </AuthFrame>
    );
  }

  const r = q.data;
  const company = r.companies.find((c) => c.orgId === (orgId ?? r.currentOrgId)) ?? r.companies[0]!;
  const allowed = (x: AppRole) => x === 'VIEWER' || company.maxRole === 'ACCOUNTANT';
  const chosen = allowed(role) ? role : 'VIEWER';
  const why = !r.writeRequested ? 'The app asked to read only.' : `Your role in ${company.name} is ${titleCase(company.role)}.`;

  async function answer(kind: 'allow' | 'deny') {
    setBusy(kind);
    setError(null);
    try {
      const res =
        kind === 'allow'
          ? await api<{ redirectTo: string }>(`/api/oauth/requests/${encodeURIComponent(r.id)}/approve`, { body: { orgId: company.orgId, role: chosen } })
          : await api<{ redirectTo: string }>(`/api/oauth/requests/${encodeURIComponent(r.id)}/deny`, { body: {} });
      setLeaving(kind === 'allow' ? 'allowed' : 'denied');
      window.location.assign(res.redirectTo);
    } catch (err) {
      setError(err);
      setBusy(null);
    }
  }

  async function switchAccount() {
    await api('/api/auth/logout', { body: {} }).catch(() => undefined);
    qc.clear();
    sessionHint.set(false);
    navigate('/login', { replace: true, state: { next: here, notice: 'Sign in with the account to connect the app to.' } });
  }

  if (leaving) {
    return (
      <AuthFrame title={leaving === 'allowed' ? `${r.app.name} is connected` : 'Not connected'} subtitle={`Returning you to ${r.app.loopback ? 'the app on this computer' : r.app.redirectHost}…`}>
        <p className="text-[13px] text-muted">If nothing happens in a few seconds, you can close this tab and go back to the app.</p>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={`Connect ${r.app.name}`} subtitle="It is asking to use your RareMargin books through RareMargin's MCP server.">
      <div className="space-y-4">
        <div className="rounded-lg border border-line bg-paper p-3 text-[12.5px]">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold text-ink">
            <Icons.Key size={14} />
            {r.app.name}
          </div>
          <div className="mt-1 text-muted">
            {r.app.verifiedHost ? (
              <>
                Identified by <span className="font-medium text-ink-2">{r.app.verifiedHost}</span>.{' '}
              </>
            ) : (
              <>This is the name the app gave itself. </>
            )}
            {r.app.loopback ? 'It receives the sign-in on this computer.' : (
              <>
                After you choose, you go back to <span className="font-medium text-ink-2">{r.app.redirectHost}</span>.
              </>
            )}
          </div>
        </div>
        {r.app.loopback && (
          <div role="note" className="rounded-lg border border-warn/20 bg-warn-soft px-3 py-2 text-[12.5px] text-warn">
            This app runs on your computer and receives the sign-in at <span className="font-mono">{r.app.redirectOrigin}</span>. Allow it only if you just started connecting RareMargin from an app on this
            computer, such as Claude Code.
          </div>
        )}

        {r.companies.length > 1 ? (
          <Field label="Company">
            <Select value={company.orgId} onChange={(e) => setOrgId(e.target.value)}>
              {r.companies.map((c) => (
                <option key={c.orgId} value={c.orgId}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field label="Company">
            <div className="text-[13.5px] font-medium">{company.name}</div>
          </Field>
        )}

        <fieldset>
          <legend className="mb-1.5 text-[12.5px] font-medium text-ink-2">What it can do</legend>
          <div className="space-y-2">
            {ROLES.map((o) => {
              const ok = allowed(o.role);
              return (
                <label
                  key={o.role}
                  className={cx('flex gap-3 rounded-lg border p-3 transition-colors', !ok ? 'cursor-not-allowed border-line opacity-60' : chosen === o.role ? 'cursor-pointer border-brand bg-brand-soft' : 'cursor-pointer border-line hover:border-line-strong')}
                >
                  <input type="radio" name="role" value={o.role} className="mt-0.5 accent-[var(--color-brand)]" checked={chosen === o.role} disabled={!ok} onChange={() => setRole(o.role)} />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-ink">
                      {o.label} <span className="font-normal text-muted">· {o.summary}</span>
                    </span>
                    <span className="block text-[12px] text-muted">{o.detail}</span>
                    {!ok && <span className="mt-0.5 block text-[12px] text-muted">{why}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <ErrorNote error={error} />
        <div className="flex gap-2">
          <Button className="flex-1" disabled={!!busy} loading={busy === 'deny'} onClick={() => answer('deny')}>
            Cancel
          </Button>
          <Button variant="primary" className="flex-1" disabled={!!busy} loading={busy === 'allow'} onClick={() => answer('allow')}>
            Allow
          </Button>
        </div>
        <p className="text-[12px] leading-relaxed text-muted">
          Signed in as <span className="text-ink-2">{r.user.email}</span>.{' '}
          <button type="button" className="font-medium text-brand hover:underline" onClick={switchAccount}>
            Use another account
          </button>
          . {r.app.name} never sees your password. Disconnect it at any time in Settings, API &amp; MCP.
        </p>
      </div>
    </AuthFrame>
  );
}
